import React, { useEffect, useState } from "react";
import { QRCodeStats } from "../types";
import { QRService } from "../services/qrService";

interface DashboardProps {
  className?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ className = "" }) => {
  const [stats, setStats] = useState<QRCodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setStats(await QRService.getQRCodeStats());
      } catch (statsError) {
        setError("Statistics are temporarily unavailable.");
        console.error("Stats error:", statsError);
      } finally {
        setLoading(false);
      }
    };
    void fetchStats();
  }, []);

  if (loading) {
    return (
      <div className={`grid gap-4 sm:grid-cols-2 xl:grid-cols-3 ${className}`}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="app-panel animate-pulse p-5">
            <div className="h-10 w-10 rounded-xl bg-slate-100" />
            <div className="mt-5 h-3 w-24 rounded bg-slate-100" />
            <div className="mt-3 h-8 w-20 rounded bg-slate-100" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div
        className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 ${className}`}
      >
        {error || "Statistics are unavailable."}
      </div>
    );
  }

  const items = [
    {
      label: "QR-Codes gesamt",
      value: stats.totalCodes,
      detail: "Aktive dynamische Links",
      tone: "blue",
      icon: (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M14 14h3v3h-3zM19 14h2v2M19 19h2v2M14 19h3v2" />
        </>
      ),
    },
    {
      label: "Scans gesamt",
      value: stats.totalAccesses,
      detail: "Über alle QR-Codes",
      tone: "violet",
      icon: (
        <>
          <path d="M3 12s3-6 9-6 9 6 9 6-3 6-9 6-9-6-9-6z" />
          <circle cx="12" cy="12" r="2.5" />
        </>
      ),
    },
    {
      label: "Neue Scans",
      value: stats.recentAccesses,
      detail: "Aktivität der letzten 24 Stunden",
      tone: "emerald",
      icon: (
        <>
          <path d="M4 17l5-5 4 4 7-8" />
          <path d="M15 8h5v5" />
        </>
      ),
    },
  ];

  return (
    <section
      className={`grid gap-4 sm:grid-cols-2 xl:grid-cols-3 ${className}`}
    >
      {items.map((item) => (
        <div key={item.label} className="app-panel stat-card p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.13em] text-slate-400">
                {item.label}
              </p>
              <p className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
                {item.value.toLocaleString()}
              </p>
              <p className="mt-2 text-sm text-slate-500">{item.detail}</p>
            </div>
            <span className={`stat-icon stat-icon-${item.tone}`}>
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {item.icon}
              </svg>
            </span>
          </div>
        </div>
      ))}
    </section>
  );
};

export default Dashboard;
